import { useEffect, useState } from 'react';
import { Avatar, Button, Drawer, Modal, Popconfirm, Spin, Tabs, Tag, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  CommentOutlined, FileTextOutlined, TeamOutlined, ExpandAltOutlined,
  EditFilled, DeleteFilled, ReloadOutlined, EyeOutlined, HistoryOutlined,
  CloseOutlined, CopyOutlined,
} from '@ant-design/icons';
import { useSelector } from '@legendapp/state/react';
import { state$ } from '../../state/globalState';
import { tachesState } from '../../state/actions/tachesState';
import { catchError } from '../../services/DaoService';
import { copyLinkToClipboard } from '../../helpers/clipboard';
import { isTacheLocked, canEditTacheStep } from '../../helpers/permissions';
import { StatutService } from '../../services/StatutService';
import { TacheService } from '../../services/TacheService';
import { useUserContext } from '../../contexts/UserContext';
import TacheDetails from './TacheDetails';
import { InlineEditableInput } from '../utils/InlineEditableInput';
import Path from '../utils/Path';
import CommentaireFeed from '../commentaire/CommentaireFeed';
import PieceJointeList from '../piecejointe/PieceJointeList';
import MembreTacheByTacheList from '../membretache/MembreTacheByTacheList';
import ActivityFeed from '../Activity/ActiviteFeed';
import LogTraces from '../logtrace/LogTraces';
import TacheEditForm from './TacheEditForm';
import AiSummarySection from '../ai/AiSummarySection';
import Moment from 'react-moment';
import styles from './TacheQuickView.module.scss';
import PropTypes from 'prop-types';

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

export default function TacheQuickView({ uid, mode = 'aside', onClose, onUpdate }) {
  const { check, currentUser } = useUserContext();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const effectiveMode = isMobile ? 'modal' : mode;

  const [localLoading, setLocalLoading] = useState(true);
  const [statuts, setStatuts] = useState([]);
  const [editVisible, setEditVisible] = useState(false);

  const tache = useSelector(() => state$.taches.list.get().find(t => t.uid === uid));

  useEffect(() => {
    if (!uid) return;
    setLocalLoading(true);
    Promise.all([
      tachesState.getTache(uid),
      StatutService.getInstance().all(),
    ])
      .then(([, s]) => setStatuts(s.data))
      .catch(catchError)
      .finally(() => setLocalLoading(false));
  }, [uid]);

  const canManageTache = currentUser?.id === tache?.user_id ||
    currentUser?.personnel?.id === tache?.responsable_id ||
    currentUser?.id === tache?.affaire?.user_id ||
    currentUser?.id === tache?.affaire?.responsable_id;
  const canEdit = canManageTache && !isTacheLocked(tache);
  const canEditStep = canEditTacheStep(tache, canManageTache);
  const canSeeLogs = canManageTache;
  const canManageMembers = canManageTache;
  const canComment = !!tache?.current_member || !!tache?.affaire?.current_membre || canManageTache;
  const canAttach = canComment;

  const handleStepChange = async (step) => {
    await tachesState.changeTacheStep(uid, step.id);
    const updated = state$.taches.list.get().find(t => t.uid === uid);
    onUpdate?.(updated);
  };

  const handleFieldChange = (value, fieldName, record) => {
    TacheService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then(r => {
        tachesState.updateTacheInList(r.data);
        onUpdate?.(r.data);
      })
      .catch(catchError);
  };

  const handleDelete = () => {
    tachesState.deleteTache(uid).then(() => onClose());
  };

  const handleFollow = () => {
    if (tache) tachesState.followTache(tache.id, currentUser.id);
  };

  const goFullPage = () => {
    navigate(`/tache/${uid}`);
    onClose();
  };

  const tabItems = tache ? [
    {
      key: 'details',
      label: 'Détails',
      children: <TacheDetails tache={tache} />,
    },
    {
      key: 'commentaires',
      label: <span><CommentOutlined /> Commentaires</span>,
      children: <CommentaireFeed parentId={tache.id} parentType={TacheService.CLASS_NAME} canCreate={canComment} />,
    },
    {
      key: 'observateurs',
      label: <span><TeamOutlined /> Observateurs</span>,
      children: <MembreTacheByTacheList tache={tache} isReadOnly={!!tache?.statut?.resolu} canManage={canManageMembers} />,
    },
    {
      key: 'documents',
      label: <span><FileTextOutlined /> Documents</span>,
      children: (
        <PieceJointeList
          parentId={tache.id}
          parentType={TacheService.CLASS_NAME}
          canAdd={canAttach}
          canRemove={canManageTache}
          canSee
        />
      ),
    },
    {
      key: 'activite',
      label: <span><HistoryOutlined /> Activité</span>,
      children: <ActivityFeed parentId={tache.id} parentType={TacheService.CLASS_NAME} />,
    },
    canSeeLogs && {
      key: 'logs',
      label: <span><FileTextOutlined /> Logs</span>,
      children: <LogTraces target="tache" parentId={tache.id} />,
    },
  ].filter(Boolean) : [];

  const bodyContent = (
    <div className={styles.container}>
      {/* Bouton fermeture flottant */}
      <button className={styles.closeBtn} onClick={onClose} aria-label="Fermer">
        <CloseOutlined />
      </button>
      {localLoading && !tache ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Spin size="large" />
        </div>
      ) : tache ? (
        <>
          {/* Priority band */}
          <div
            className={styles.priorityBand}
            style={{ background: tache.priorite?.couleur ?? '#3E65AC' }}
          />

          {/* Header */}
          <div className={styles.header}>
            <div className={styles.tagsRow}>
              <Tooltip title="Copier le lien de la tâche">
                <span
                  className={styles.codeTag}
                  onClick={() => copyLinkToClipboard(`/tache/${tache.uid}`, 'Lien de la tâche')}
                  style={{ cursor: 'pointer' }}
                >
                  {tache.code} <CopyOutlined style={{ fontSize: 11 }} />
                </span>
              </Tooltip>
              {tache.statut?.nom && (
                <Tag color={tache.statut.couleur} style={{ margin: 0 }}>{tache.statut.nom}</Tag>
              )}
              {tache.priorite?.nom && (
                <Tag color={tache.priorite.couleur} style={{ margin: 0 }}>{tache.priorite.nom}</Tag>
              )}
              {tache.retard && !tache.statut?.resolu && (
                <Tag color="red" style={{ margin: 0 }}>En retard</Tag>
              )}
            </div>

            <h2 className={styles.title}>
              <InlineEditableInput
                canEdit={canEdit}
                type="text"
                fieldName="nom"
                value={tache.nom}
                record={tache}
                onChange={handleFieldChange}
              >
                {tache.nom ?? '—'}
              </InlineEditableInput>
            </h2>

            <div className={styles.metaRow}>
              {tache.responsable && (
                <span className={styles.metaItem}>
                  <Avatar size={18} src={tache.responsable.avatar_link}>
                    {tache.responsable.name?.[0]}
                  </Avatar>
                  <span>{tache.responsable.name}</span>
                </span>
              )}
              {tache.date_limite && (
                <span className={`${styles.metaItem} ${tache.retard ? styles.metaRetard : ''}`}>
                  <Moment format="DD MMM YYYY">{tache.date_limite}</Moment>
                </span>
              )}
              {tache.affaire && check('VIEW-AFFAIRE') && (
                <span className={styles.metaItem}>
                  <span
                    className={styles.affaireLink}
                    onClick={() => { navigate(`/affaire/${tache.affaire.uid}`); onClose(); }}
                  >
                    {tache.affaire.nom}
                  </span>
                </span>
              )}
            </div>

            <div className={styles.actions}>
              <Tooltip title="Rafraîchir">
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => tachesState.getTache(uid)}
                />
              </Tooltip>
              {!tache.current_member && canManageTache && (
                <Tooltip title="Suivre la tâche">
                  <Button size="small" icon={<EyeOutlined />} onClick={handleFollow}>
                    Suivre
                  </Button>
                </Tooltip>
              )}
              {canEdit && (
                <Button
                  type="primary"
                  size="small"
                  icon={<EditFilled />}
                  onClick={() => setEditVisible(true)}
                >
                  Éditer
                </Button>
              )}
              {canEdit && (
                <Popconfirm
                  title="Supprimer cette tâche ?"
                  onConfirm={handleDelete}
                  okText="Supprimer"
                  okButtonProps={{ danger: true }}
                  cancelText="Annuler"
                >
                  <Button danger size="small" icon={<DeleteFilled />} />
                </Popconfirm>
              )}
              <Tooltip title="Ouvrir la page complète">
                <Button
                  type="text"
                  size="small"
                  icon={<ExpandAltOutlined />}
                  className={styles.openPageBtn}
                  onClick={goFullPage}
                >
                  {!isMobile && 'Page complète'}
                </Button>
              </Tooltip>
              <Tooltip title="Fermer">
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={onClose}
                />
              </Tooltip>
            </div>
          </div>

          {/* Résumé IA (section inline) */}
          <div className={styles.summaryInline}>
            <AiSummarySection type="tache" uid={uid} />
          </div>

          {/* Statut progression */}
          {statuts.length > 0 && (
            <div className={styles.pathSection}>
              <Path
                currentStep={tache?.statut?.code}
                steps={statuts}
                onCurrentPathChange={handleStepChange}
                readOnly={!canEditStep}
              />
            </div>
          )}

          {/* Tabs */}
          <Tabs
            defaultActiveKey="details"
            items={tabItems}
            className={styles.tabs}
            size="small"
          />
        </>
      ) : null}
    </div>
  );

  const drawerWidth = Math.min(960, window.innerWidth - 32);

  return (
    <>
      {effectiveMode === 'aside' ? (
        <Drawer
          open={!!uid}
          onClose={onClose}
          placement="right"
          width={drawerWidth}
          className={styles.drawer}
          title={null}
          closeIcon={null}
          destroyOnClose
        >
          {bodyContent}
        </Drawer>
      ) : (
        <Modal
          open={!!uid}
          onCancel={onClose}
          width={Math.min(1180, window.innerWidth - 32)}
          footer={null}
          title={null}
          closeIcon={null}
          className={styles.modal}
          destroyOnClose
        >
          {bodyContent}
        </Modal>
      )}

      {editVisible && tache && (
        <TacheEditForm
          visible={editVisible}
          uid={uid}
          onUpdate={async () => {
            setEditVisible(false);
            try {
              const res = await TacheService.getInstance().find(uid);
              tachesState.updateTacheInList(res.data);
              onUpdate?.(res.data);
            } catch (e) { catchError(e); }
          }}
          onCancel={() => setEditVisible(false)}
        />
      )}
    </>
  );
}

TacheQuickView.propTypes = {
  uid: PropTypes.string,
  mode: PropTypes.oneOf(['aside', 'modal']),
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
};
